import React, { useMemo } from 'react';
import {
    SimpleShowLayout,
    TextField,
    ReferenceField,
    FunctionField,
    NumberField,
    usePermissions,
    WrapperField,
    useTranslate,
    useListContext,
} from 'react-admin';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import GroupListActions from '@/components/deviceGroup/GroupListActions.jsx';
import IsActiveField from '@/components/global/fields/IsActiveField.jsx';
import GroupMoreActionsPopover from '@/components/deviceGroup/GroupMoreActionsPopover.jsx';
import DeviceUptimeField from '@/components/device/DeviceUptimeField.jsx';
import { GroupBulkActions } from '@/components/deviceGroup/GroupBulkActions.jsx';
import { ReferenceArrayFilter, BooleanFilter, PeriodFilter } from '@/components/global/filters/filters.jsx';
import FromToFilterComponent from '@/components/global/filters/FromToFilterComponent.jsx';
import ContractorField from '@/components/contractors/ContractorField.jsx';
import { useNavigate } from 'react-router-dom';
import { useDrawer } from '@/composables/useDrawer.js';
import { aliasToString } from '@/helpers/device/softwareHelpers.js';
import { DEFAULT_PERIOD } from '@/components/analytics/PeriodFilters.jsx';

const TransactionsClickable = ({ record }) => {
    const navigate = useNavigate();
    const { filterValues } = useListContext();
    const currentPeriod = filterValues?.period ?? DEFAULT_PERIOD;
    const source = `analytics[${currentPeriod}].transactionsCount`;

    const handleClick = e => {
        e.stopPropagation();
        const filter = encodeURIComponent(JSON.stringify({ 'device->group->id_in': [record.id] }));
        navigate(`/transaction?filter=${filter}`);
    };

    return (
        <Box sx={{ cursor: 'pointer' }} onClick={handleClick} role='button'>
            <NumberField record={record} source={source} emptyText='0' />
        </Box>
    );
};

const BagsClickable = ({ record }) => {
    const navigate = useNavigate();
    const { filterValues } = useListContext();
    const currentPeriod = filterValues?.period ?? DEFAULT_PERIOD;
    const source = `analytics[${currentPeriod}].bagsCount`;

    const handleClick = e => {
        e.stopPropagation();
        const filter = encodeURIComponent(JSON.stringify({ 'device->groupId_in': [record.id] }));
        navigate(`/bag?filter=${filter}`);
    };

    return (
        <Box sx={{ cursor: 'pointer' }} onClick={handleClick} role='button'>
            <NumberField record={record} source={source} emptyText='0' />
        </Box>
    );
};

export const DeviceGroupList = () => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();

    const filters = useMemo(
        () => [
            <PeriodFilter key='period' alwaysOn={true} className='period-filter' />,
            permissions.isAdmin && (
                <ReferenceArrayFilter
                    name='contractorId_in'
                    key='contractorId_in'
                    source='contractorId_in'
                    reference='contractor'
                    label={translate('common.contractor')}
                    forceDesktopOnMobile={true}
                    alwaysOn={isMobile}
                />
            ),
            <FromToFilterComponent
                key='deviceCount'
                source='deviceCount_range'
                label={translate('resources.deviceGroup.filters.deviceCount')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            permissions.isAdmin && (
                <FromToFilterComponent
                    key='newDeviceCount'
                    source='newDeviceCount_range'
                    label={translate('resources.deviceGroup.filters.newDeviceCount')}
                    forceDesktopOnMobile={true}
                    alwaysOn={isMobile}
                />
            ),
            <FromToFilterComponent
                key='uptime'
                source='analytics->uptime_range'
                label={translate('resources.collectionPoint.filters.uptime')}
                type='percent'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='transactionsCount'
                source='analytics->transactionsCount_range'
                label={translate('common.transactionsNumber')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='bagsCount'
                source='analytics->bagsCount_range'
                label={translate('resources.collectionPoint.filters.bagsCount')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <BooleanFilter name='isActive' key='isActive' source='isActive' label={translate('common.active')} alwaysOn={isMobile} />,
            <FromToFilterComponent
                key='createdAt'
                source='createdAt_range'
                label={translate('common.createdAtFilter')}
                type='date'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='updatedAt'
                source='updatedAt_range'
                label={translate('common.updatedAtFilter')}
                type='date'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='aliasSoc'
                source='targetSoftwareSocId_in'
                reference='software-alias'
                optionText={aliasToString}
                label={translate('resources.deviceGroup.filters.targetSoc')}
                filter={{ 'software.type': 'SOC' }}
                searchFields={['name', 'software.version']}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='aliasMcu'
                source='targetSoftwareMcuId_in'
                reference='software-alias'
                optionText={aliasToString}
                label={translate('resources.deviceGroup.filters.targetMcu')}
                filter={{ 'software.type': 'MCU' }}
                searchFields={['name', 'software.version']}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [isMobile, permissions.isAdmin, translate],
    ).filter(Boolean);

    if (isLoading) return null;

    return (
        <CustomList filters={filters} actions={<GroupListActions />} addLastColumnShadow={true}>
            <CustomDatagridConfigurable
                bulkActionButtons={<GroupBulkActions />}
                rowClick={id => {
                    openDrawer(id, 'device-group-show');
                    return false;
                }}
            >
                <TextField source='name' label={translate('resources.deviceGroup.fields.name')} />
                {permissions.isAdmin ? (
                    <FunctionField
                        label={translate('common.contractor')}
                        sortBy='contractor.name'
                        render={record => <ContractorField contractor={record.contractor} />}
                    />
                ) : null}
                <NumberField source='deviceCount' label={translate('resources.deviceGroup.fields.deviceCount')} />
                {permissions.isAdmin ? <NumberField source='newDeviceCount' label={translate('resources.deviceGroup.fields.newDeviceCount')} /> : null}
                <FunctionField
                    label={translate('common.uptime')}
                    source='analytics.uptime'
                    render={record => <DeviceUptimeField className='' record={record} />}
                />
                <FunctionField
                    source='analytics.transactionsCount'
                    label={translate('common.transactionsCount')}
                    render={record => <TransactionsClickable record={record} />}
                />
                <FunctionField source='analytics.bagsCount' label={translate('common.bagsCount')} render={record => <BagsClickable record={record} />} />
                <FunctionField label={translate('common.active')} render={record => <IsActiveField record={record} />} sortBy='isActive' />
                {permissions.isAdmin ? (
                    <WrapperField
                        source='targetSoftwareSoc'
                        label={translate('resources.deviceGroup.labels.software')}
                        sortBy='targetSoftwareSoc.software.version'
                    >
                        <Box display='flex'>
                            <SimpleShowLayout flex={1}>
                                <ReferenceField
                                    source='targetSoftwareMcuId'
                                    label={translate('resources.device.labels.mcu')}
                                    reference='software-alias'
                                    link=''
                                    sortable={false}
                                />
                            </SimpleShowLayout>
                            <SimpleShowLayout flex={1}>
                                <ReferenceField
                                    source='targetSoftwareSocId'
                                    label={translate('resources.device.labels.soc')}
                                    reference='software-alias'
                                    link=''
                                    sortable={false}
                                />
                            </SimpleShowLayout>
                        </Box>
                    </WrapperField>
                ) : null}
                <FunctionField
                    label={translate('common.options')}
                    onClick={e => e.stopPropagation()}
                    render={record => <GroupMoreActionsPopover permissions={permissions} groupId={record.id} />}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};
